// Request Time Off modal — opens from the Home hero card and the
// Profile / Time Off tab (T-074, T-259).
//
// Single-form modal that drives all three mutation flows:
//   - **POST** when no `editingRequest` is passed
//   - **PATCH** when `editingRequest` is passed (Pending only)
//   - **422 soft-warning confirm dialog** in the same modal — show
//     warnings, "Confirm" re-submits with `confirm: true`
//
// Visual layout (T-259, owner screenshot):
//   - Purple gradient header strip with eyebrow ("NEW REQUEST" /
//     "EDIT REQUEST") + bold title + close-X button
//   - Body: ABSENCE TYPE dropdown showing policy emoji + name,
//     START DATE / END DATE grid (DD-MM-YYYY placeholder), optional
//     NOTE textarea
//   - Footer: Cancel button (outlined slate) + Submit request
//     button (filled slate-900). Submit disabled until both dates
//     are filled in.
//
// Validation errors (400) render inline. Other errors render in a
// banner. Closing the modal cancels the in-flight mutation cleanly
// via React Query's signal.

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar as CalendarIcon, X as XIcon } from 'lucide-react';
import type {
  TimeOffPolicy,
  TimeOffRequestCreateRequest,
  TimeOffRequestCreateResponse,
  TimeOffRequestSummary,
  TimeOffSoftWarning,
} from '@pa/shared';
import { api, ApiError } from '../../../../api/client';
import { countWorkingDays, parseIsoDateLocal } from './countWorkingDays';

// T-261 — character-counter thresholds (spec §8.1). Hidden until
// 80% of the limit so the typical short-note case stays clean.
const NOTE_MAX = 500;
const NOTE_COUNTER_VISIBLE_AT = Math.floor(NOTE_MAX * 0.8); // 400
const NOTE_COUNTER_AMBER_AT = Math.floor(NOTE_MAX * 0.95); // 475

// All focusable selectors we need to enumerate for the Tab-cycle
// focus trap. Mirrors the standard interactive-elements list — links
// are excluded because the modal doesn't render any. `[tabindex="-1"]`
// is filtered out below so we don't trap on elements that opt out.
const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface RequestModalProps {
  employeeId: string;
  policies: TimeOffPolicy[];
  // When set, the modal opens in EDIT mode (PATCH /:id). Otherwise
  // it's CREATE mode (POST).
  editingRequest?: TimeOffRequestSummary;
  /**
   * T-261 — Calendar-day-click entry (spec §2 row 3). Pre-fills
   * both Start and End to this date so the modal opens as a
   * one-day request on the clicked day. ISO `yyyy-mm-dd`.
   */
  preFilledDate?: string;
  /**
   * T-261 — eyebrow text override (spec §2). Overrides the default
   * `NEW REQUEST` / `EDIT REQUEST`. Used for entries that need a
   * contextual hint, e.g. `NEW REQUEST · 15 MAY` from a calendar
   * click. Ignored in edit mode.
   */
  eyebrowOverride?: string;
  /**
   * T-264 — HR-on-behalf mode (spec §11.1). Set true when the
   * caller is an OWNER/ADMIN submitting for an employee OTHER
   * than themselves. Causes:
   *   - the eyebrow to default to
   *     `NEW REQUEST · ON BEHALF OF {targetEmployeeName}` (when
   *     `eyebrowOverride` isn't set);
   *   - `onBehalfOf: true` to be sent in the POST body, which
   *     the server treats as auto-approve when role==='admin'.
   * The server enforces RBAC — this flag is for audit + the §7.4
   * UI flavor (see shared type comment).
   */
  isOnBehalfOf?: boolean;
  /**
   * T-264 — display name of the target employee, used in the
   * default HR-on-behalf eyebrow string. Ignored unless
   * `isOnBehalfOf === true`. Falls back to `someone else` if not
   * provided.
   */
  targetEmployeeName?: string;
  /**
   * T-261 — Fires after a successful create with the server
   * response. The modal still calls `onClose` immediately after;
   * the parent uses this hook to mount a transient success toast
   * (with 5-second Undo) outside the modal scope (spec §9.3 step 6).
   *
   * Not called for the edit path (PATCH) — only fresh submissions.
   */
  onSubmitted?: (response: TimeOffRequestCreateResponse) => void;
  onClose: () => void;
}

interface FormState {
  policyId: string;
  startDate: string;
  endDate: string;
  note: string;
}

export function RequestModal(props: RequestModalProps) {
  const {
    employeeId,
    policies,
    editingRequest,
    preFilledDate,
    eyebrowOverride,
    isOnBehalfOf,
    targetEmployeeName,
    onSubmitted,
    onClose,
  } = props;
  const queryClient = useQueryClient();
  const initialForm = useMemo<FormState>(
    () =>
      editingRequest
        ? {
            policyId: editingRequest.policyId,
            startDate: editingRequest.startDate,
            endDate: editingRequest.endDate,
            note: editingRequest.note ?? '',
          }
        : {
            policyId: policies[0]?.id ?? '',
            // T-261 — `preFilledDate` lands on both start AND end so
            // the calendar-day-click entry opens with a one-day
            // request already shaped (per spec §2 row 3).
            startDate: preFilledDate ?? '',
            endDate: preFilledDate ?? '',
            note: '',
          },
    [editingRequest, policies, preFilledDate],
  );
  const [form, setForm] = useState<FormState>(initialForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [pendingWarnings, setPendingWarnings] = useState<
    TimeOffSoftWarning[] | null
  >(null);
  const [formError, setFormError] = useState<string | null>(null);
  // T-260 — discard-changes confirm dialog state. Triggered by
  // Escape or backdrop click only; X + Cancel discard immediately.
  const [discardPromptOpen, setDiscardPromptOpen] = useState(false);

  // T-260 — focus-trap + restore. Capture the element that had focus
  // when the modal mounted so we can restore it on close.
  const cardRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  // Stable ids so the dialog can advertise its title + eyebrow to AT.
  const titleId = useId();
  const eyebrowId = useId();

  // Modal is "dirty" if anything diverges from the initial form
  // values. Pure derivation off `form` + `initialForm` — no extra
  // state to keep in sync.
  const isDirty =
    form.policyId !== initialForm.policyId ||
    form.startDate !== initialForm.startDate ||
    form.endDate !== initialForm.endDate ||
    form.note !== initialForm.note;

  const submit = useMutation({
    mutationFn: async (confirm: boolean) => {
      // T-261 — End date is optional (spec §6.3). When blank, send
      // it equal to Start so the server treats it as a single-day
      // request.
      const effectiveEnd =
        form.endDate.length > 0 ? form.endDate : form.startDate;
      const body: TimeOffRequestCreateRequest & { confirm?: boolean } = {
        employeeId,
        policyId: form.policyId,
        startDate: form.startDate,
        endDate: effectiveEnd,
      };
      if (form.note.trim().length > 0) body.note = form.note.trim();
      if (confirm) body.confirm = true;
      // T-264 — HR-on-behalf flag. Server uses this to auto-approve
      // when the caller is an admin posting for another employee
      // (spec §11.1) and to audit the on-behalf actor.
      if (isOnBehalfOf) body.onBehalfOf = true;
      if (editingRequest) {
        return api.patch<TimeOffRequestCreateResponse>(
          `/api/timeoff/${editingRequest.id}`,
          body,
        );
      }
      return api.post<TimeOffRequestCreateResponse>(`/api/timeoff`, body);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['timeoff-tab', employeeId] });
      queryClient.invalidateQueries({
        queryKey: ['timeoff-policies', employeeId],
      });
      // T-261 — fire the parent's toast hook before closing so the
      // toast can drive Undo against the just-created request id.
      // Skipped on edit since Undo wouldn't make sense there.
      if (!editingRequest && onSubmitted) {
        try {
          onSubmitted(res);
        } catch {
          // Swallow — toast plumbing isn't allowed to block close.
        }
      }
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        if (err.status === 400) {
          setFieldErrors(
            (err.body as { issues?: { fieldErrors?: Record<string, string[]> } })
              ?.issues?.fieldErrors ?? {},
          );
          setFormError(null);
          return;
        }
        if (err.status === 422) {
          const warnings = (err.body as { warnings?: TimeOffSoftWarning[] })
            ?.warnings;
          if (warnings) {
            setPendingWarnings(warnings);
            setFieldErrors({});
            setFormError(null);
            return;
          }
        }
        setFormError(err.message);
        return;
      }
      setFormError(err instanceof Error ? err.message : 'Submission failed');
    },
  });

  function reset() {
    setFieldErrors({});
    setPendingWarnings(null);
    setFormError(null);
  }

  // Reset transient error/warning state when the user mutates the form.
  useEffect(() => {
    if (pendingWarnings) setPendingWarnings(null);
  }, [form.startDate, form.endDate, form.policyId]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    reset();
    submit.mutate(false);
  }

  function handleConfirmWarnings() {
    submit.mutate(true);
  }

  // ── T-260 a11y plumbing ────────────────────────────────────────────
  //
  // Capture the opener on mount + restore focus on unmount. We grab
  // `document.activeElement` synchronously inside the effect so the
  // ref reflects whoever triggered the modal — typically the
  // "Request time off" CTA on the hero or the calendar cell.
  useEffect(() => {
    openerRef.current = (document.activeElement as HTMLElement | null) ?? null;
    // Focus the close button so Tab cycles forward through the form.
    // We defer one tick so the card has mounted into the DOM.
    queueMicrotask(() => {
      const el = cardRef.current?.querySelector<HTMLElement>(
        '[data-testid="request-modal-close"]',
      );
      el?.focus();
    });
    return () => {
      // Defer restore one tick so the underlying re-render (modal
      // unmount → trigger remounts) doesn't clobber the focus.
      const opener = openerRef.current;
      if (opener && typeof opener.focus === 'function') {
        queueMicrotask(() => opener.focus());
      }
    };
  }, []);

  // Close-or-confirm helper. X / Cancel call `onClose` directly so
  // they always discard immediately (spec §2). Escape + backdrop
  // route through here so a dirty modal asks "Discard changes?"
  // first.
  function closeIfClean() {
    if (isDirty) {
      setDiscardPromptOpen(true);
    } else {
      onClose();
    }
  }

  // Global Escape handler. Lives on `document` so it fires regardless
  // of which element inside the modal has focus.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      // If the discard confirm is already up, Escape dismisses it
      // (keep editing) — not the whole modal.
      if (discardPromptOpen) {
        e.preventDefault();
        setDiscardPromptOpen(false);
        return;
      }
      e.preventDefault();
      closeIfClean();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [discardPromptOpen, isDirty]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tab focus trap. Scoped to the card via the keydown listener
  // attached in JSX (we want the card itself to capture, not the
  // backdrop, so a click on the backdrop doesn't steal Tab).
  function handleCardKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'Tab') return;
    const root = cardRef.current;
    if (!root) return;
    const focusables = Array.from(
      root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => !el.hasAttribute('data-focus-trap-ignore'));
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey) {
      if (active === first || !root.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  // Backdrop click. We compare the click target to the backdrop's
  // own element so clicks inside the card don't bubble up as a
  // close request.
  function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) {
      closeIfClean();
    }
  }

  const isEdit = Boolean(editingRequest);
  // T-261 + T-264 — eyebrow resolution order (highest precedence
  // first):
  //   1. Edit mode → `EDIT REQUEST` (overrides + on-behalf are
  //      ignored on the edit path).
  //   2. Caller-provided `eyebrowOverride` (e.g. calendar-day click
  //      sets `NEW REQUEST · 15 MAY`).
  //   3. HR-on-behalf default → `NEW REQUEST · ON BEHALF OF NAME`
  //      (T-264, spec §11.1).
  //   4. Plain `NEW REQUEST`.
  const onBehalfName =
    targetEmployeeName && targetEmployeeName.trim().length > 0
      ? targetEmployeeName.trim().toUpperCase()
      : 'SOMEONE ELSE';
  const eyebrow = isEdit
    ? 'EDIT REQUEST'
    : eyebrowOverride && eyebrowOverride.length > 0
      ? eyebrowOverride
      : isOnBehalfOf
        ? `NEW REQUEST · ON BEHALF OF ${onBehalfName}`
        : 'NEW REQUEST';
  const title = isEdit ? 'Edit Time Off' : 'Request Time Off';
  const submitLabel = isEdit ? 'Save changes' : 'Submit request';
  // T-261 — Submit needs a policy + a start date. End is optional
  // (single-day when blank). When End < Start, the inline error
  // below renders and we block submit.
  const startD = parseIsoDateLocal(form.startDate);
  const endD = form.endDate ? parseIsoDateLocal(form.endDate) : null;
  const endBeforeStart = !!(startD && endD && endD < startD);
  const submitDisabled =
    submit.isPending ||
    !form.policyId ||
    !form.startDate ||
    endBeforeStart;
  // T-263 — duration probe. Calls `/api/timeoff/duration` so the
  // pill is holiday-aware + matches what the server will compute on
  // submit. Falls back to the local Mon-Fri count while the query
  // is in flight or if the probe errors (so the pill never goes
  // blank — same number the user expects). Skipped when the form
  // is invalid (no start / end<start).
  const localWorkingDays = countWorkingDays(form.startDate, form.endDate);
  const probeEnabled =
    !!form.startDate && parseIsoDateLocal(form.startDate) !== null && !endBeforeStart;
  const durationProbe = useQuery({
    queryKey: [
      'timeoff-duration',
      employeeId,
      form.startDate,
      form.endDate || form.startDate,
    ],
    queryFn: () =>
      api.get<{ workingDays: number }>(
        `/api/timeoff/duration?employeeId=${encodeURIComponent(
          employeeId,
        )}&start=${encodeURIComponent(form.startDate)}&end=${encodeURIComponent(
          form.endDate || form.startDate,
        )}`,
      ),
    enabled: probeEnabled,
    // Stay on the previous value when the inputs change so the pill
    // doesn't flicker back to the local fallback between probes.
    placeholderData: (prev) => prev,
    // Per-key cache means each (start, end) probe is fetched once
    // even if the user toggles between values.
    staleTime: 60_000,
  });
  const workingDays =
    durationProbe.data?.workingDays ?? localWorkingDays;
  // T-261 — character counter thresholds for the note (spec §8.1).
  const noteLen = form.note.length;
  const noteCounterVisible = noteLen >= NOTE_COUNTER_VISIBLE_AT;
  const noteCounterColor =
    noteLen >= NOTE_MAX
      ? 'text-rose-600'
      : noteLen >= NOTE_COUNTER_AMBER_AT
        ? 'text-amber-600'
        : 'text-slate-400';

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4"
      data-testid="timeoff-request-modal"
      onMouseDown={handleBackdropClick}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={eyebrowId}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
        onKeyDown={handleCardKeyDown}
      >
        {/* Purple gradient header (T-259) */}
        <div
          className="relative px-6 py-5 text-white"
          style={{
            background:
              'linear-gradient(135deg, #6344C6 0%, #8B6DE8 100%)',
          }}
          data-testid="request-modal-header"
        >
          <p
            id={eyebrowId}
            className="text-xs font-semibold uppercase tracking-widest text-white/70"
          >
            {eyebrow}
          </p>
          <h2
            id={titleId}
            className="text-2xl font-bold leading-tight mt-1"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="request-modal-close"
            aria-label="Close"
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition"
          >
            <XIcon size={18} aria-hidden />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {discardPromptOpen ? (
            <DiscardPromptPanel
              onDiscard={onClose}
              onKeepEditing={() => setDiscardPromptOpen(false)}
            />
          ) : pendingWarnings ? (
            <SoftWarningPanel
              warnings={pendingWarnings}
              onConfirm={handleConfirmWarnings}
              onCancel={() => setPendingWarnings(null)}
              saving={submit.isPending}
            />
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit}>
              <Field
                label="Absence type"
                error={fieldErrors.policyId?.[0]}
              >
                <SelectShell hasError={!!fieldErrors.policyId?.[0]}>
                  <select
                    className="w-full appearance-none bg-transparent outline-none text-slate-900 font-semibold"
                    value={form.policyId}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, policyId: e.target.value }))
                    }
                    data-testid="request-modal-policy"
                    disabled={!!editingRequest}
                  >
                    {policies.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.emoji} {p.name}
                      </option>
                    ))}
                  </select>
                </SelectShell>
              </Field>

              {/* T-260 — stack below 480px (`min-[480px]:`); side-by-side
                  on every other viewport. Matches spec §161. */}
              <div
                className="grid grid-cols-1 min-[480px]:grid-cols-2 gap-3"
                data-testid="request-modal-dates-row"
              >
                <Field
                  label="Start date"
                  error={fieldErrors.startDate?.[0]}
                >
                  <DateInputShell hasError={!!fieldErrors.startDate?.[0]}>
                    <CalendarIcon
                      size={16}
                      className="text-slate-400 pointer-events-none"
                      aria-hidden
                    />
                    <input
                      type="date"
                      lang="en-GB"
                      placeholder="DD-MM-YYYY"
                      className="flex-1 bg-transparent outline-none text-slate-900 font-semibold placeholder:text-slate-400"
                      value={form.startDate}
                      onChange={(e) =>
                        setForm((s) => ({ ...s, startDate: e.target.value }))
                      }
                      data-testid="request-modal-start"
                    />
                  </DateInputShell>
                </Field>
                <Field
                  label={
                    <>
                      End date{' '}
                      <span className="text-slate-400 normal-case font-normal">
                        (optional)
                      </span>
                    </>
                  }
                  error={fieldErrors.endDate?.[0]}
                >
                  <DateInputShell
                    hasError={!!fieldErrors.endDate?.[0] || endBeforeStart}
                  >
                    <CalendarIcon
                      size={16}
                      className="text-slate-400 pointer-events-none"
                      aria-hidden
                    />
                    <input
                      type="date"
                      lang="en-GB"
                      placeholder="DD-MM-YYYY"
                      className="flex-1 bg-transparent outline-none text-slate-900 font-semibold placeholder:text-slate-400"
                      value={form.endDate}
                      onChange={(e) =>
                        setForm((s) => ({ ...s, endDate: e.target.value }))
                      }
                      data-testid="request-modal-end"
                    />
                  </DateInputShell>
                </Field>
              </div>

              {/* T-261 — duration pill + inline cross-date error
                  (spec §7 + §7.2). Pill only renders when Start is
                  set. Error renders when End < Start. */}
              {form.startDate && (
                <div className="flex items-center gap-3 -mt-2">
                  <span
                    data-testid="request-modal-duration-pill"
                    className="inline-flex items-center gap-1.5 bg-[#6344C6]/10 text-[#6344C6] text-xs font-bold px-3 py-1.5 rounded-full"
                  >
                    {workingDays} working day{workingDays === 1 ? '' : 's'}
                  </span>
                  {endBeforeStart && (
                    <span
                      data-testid="request-modal-end-before-start-error"
                      className="text-xs font-medium text-rose-600"
                      role="alert"
                    >
                      End date must be after start date
                    </span>
                  )}
                </div>
              )}

              <Field
                label={
                  <>
                    Note <span className="text-slate-400 normal-case font-normal">(optional)</span>
                  </>
                }
                error={fieldErrors.note?.[0]}
              >
                <div className="relative">
                  <textarea
                    rows={3}
                    maxLength={NOTE_MAX}
                    placeholder="Add a reason or any details…"
                    aria-label={`Note (optional, up to ${NOTE_MAX} characters)`}
                    className="block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#6344C6] focus:outline-none focus:ring-2 focus:ring-[#6344C6]/30"
                    value={form.note}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, note: e.target.value }))
                    }
                    data-testid="request-modal-note"
                  />
                  {/* T-261 — character counter (spec §8.1). Hidden
                      until 80% of the limit. Colors flip at 95% +
                      100%. maxLength on the textarea hard-stops
                      input at 500. */}
                  {noteCounterVisible && (
                    <span
                      data-testid="request-modal-note-counter"
                      aria-hidden="true"
                      className={`absolute bottom-2 right-3 text-xs font-medium ${noteCounterColor}`}
                    >
                      {noteLen}/{NOTE_MAX}
                    </span>
                  )}
                </div>
              </Field>

              {formError && (
                <div
                  className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-xl p-3"
                  data-testid="request-modal-error"
                >
                  {formError}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-1">
                <button
                  type="button"
                  className="px-5 py-2.5 rounded-xl text-sm font-bold text-slate-700 border border-slate-200 hover:bg-slate-50"
                  onClick={onClose}
                  disabled={submit.isPending}
                  data-testid="request-modal-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl text-sm font-bold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={submitDisabled}
                  data-testid="request-modal-submit"
                >
                  {submit.isPending ? 'Submitting…' : submitLabel}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: React.ReactNode;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
        {label}
      </span>
      {children}
      {error && (
        <span className="block text-xs text-rose-600 mt-1">{error}</span>
      )}
    </label>
  );
}

function SelectShell({
  hasError,
  children,
}: {
  hasError: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={
        'flex items-center gap-2 rounded-xl border bg-[#F3F0FB] px-4 py-3 ' +
        (hasError
          ? 'border-rose-300'
          : 'border-[#6344C6]/40 focus-within:border-[#6344C6] focus-within:ring-2 focus-within:ring-[#6344C6]/30')
      }
    >
      {children}
      <ChevronDown />
    </div>
  );
}

function DateInputShell({
  hasError,
  children,
}: {
  hasError: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={
        'flex items-center gap-2 rounded-xl border bg-white px-4 py-3 ' +
        (hasError
          ? 'border-rose-300'
          : 'border-slate-200 focus-within:border-[#6344C6] focus-within:ring-2 focus-within:ring-[#6344C6]/30')
      }
    >
      {children}
      <ChevronDown />
    </div>
  );
}

function ChevronDown() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      aria-hidden
      className="text-slate-400 flex-shrink-0"
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth={2}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// T-260 — discard-changes confirm. Shown when Esc / backdrop click
// fires while the modal is dirty (any field differs from its initial
// value). X + Cancel buttons skip this and call `onClose` directly.
function DiscardPromptPanel({
  onDiscard,
  onKeepEditing,
}: {
  onDiscard: () => void;
  onKeepEditing: () => void;
}) {
  return (
    <div className="space-y-3" data-testid="request-modal-discard-prompt">
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-1">
        <p className="text-sm font-semibold text-slate-900">
          Discard changes?
        </p>
        <p className="text-xs text-slate-500">
          You've started a request — closing now drops what you entered.
        </p>
      </div>
      <div className="flex justify-end gap-3">
        <button
          type="button"
          className="px-5 py-2.5 rounded-xl text-sm font-bold text-slate-700 border border-slate-200 hover:bg-slate-50"
          onClick={onKeepEditing}
          data-testid="request-modal-discard-keep"
        >
          Keep editing
        </button>
        <button
          type="button"
          className="px-5 py-2.5 rounded-xl text-sm font-bold bg-rose-600 text-white hover:bg-rose-700"
          onClick={onDiscard}
          data-testid="request-modal-discard-confirm"
        >
          Discard
        </button>
      </div>
    </div>
  );
}

function SoftWarningPanel({
  warnings,
  onConfirm,
  onCancel,
  saving,
}: {
  warnings: TimeOffSoftWarning[];
  onConfirm: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  return (
    <div
      className="space-y-3"
      data-testid="request-modal-soft-warnings"
    >
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 space-y-2">
        <p className="text-sm font-semibold text-amber-900">Please confirm:</p>
        <ul className="list-disc pl-5 space-y-1 text-sm text-amber-900">
          {warnings.map((w) => (
            <li key={w.code}>{w.message}</li>
          ))}
        </ul>
      </div>
      <div className="flex justify-end gap-3">
        <button
          type="button"
          className="px-5 py-2.5 rounded-xl text-sm font-bold text-slate-700 border border-slate-200 hover:bg-slate-50"
          onClick={onCancel}
          disabled={saving}
        >
          Go back
        </button>
        <button
          type="button"
          className="px-5 py-2.5 rounded-xl text-sm font-bold bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
          onClick={onConfirm}
          disabled={saving}
          data-testid="request-modal-confirm-warnings"
        >
          {saving ? 'Submitting…' : 'Submit anyway'}
        </button>
      </div>
    </div>
  );
}
